import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { getLoggedinUser } from "../../../helpers/api_helper";
import {
  createCampaign,
  fetchCampaignHtmlFromUrl,
  getCampaign,
  updateCampaign,
} from "../../../helpers/campaigns_helper";
// Home “Continuar edição” — lembra a última campanha tocada (localStorage).
import { rememberLastCampaignEdit } from "../../Dashboard/dashboardPrefs";
import {
  checkCampaignDeliverability,
  extractTrackableUrlsFromHtml,
  type DeliverabilityCheckResult,
} from "../../../helpers/deliverability_helper";
import CampaignEasyEmailEditor from "./CampaignEasyEmailEditor";
import PreflightPanel from "./PreflightPanel";

type FormValues = {
  name: string;
  subject: string;
  html_body: string;
  editor_design: Record<string, unknown> | null;
  goal_urls: string[];
  use_brand_kit: boolean;
};

type ContentTab = "editor" | "source" | "import";

const AUTOSAVE_MS = 1500;
/** PATCH silencioso no servidor — mais lento que o rascunho local (LAC-112). */
const HTTP_AUTOSAVE_MS = 3500;
const PLACEHOLDERS = ["{{nome}}", "{{email}}", "{{telefone}}"] as const;

type DraftPayload = FormValues;

const draftStorageKey = (campaignId: string | undefined): string =>
  campaignId
    ? `brenvio.campaign.draft.${campaignId}`
    : "brenvio.campaign.draft.new";

const readDraft = (key: string): DraftPayload | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as DraftPayload;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    return {
      name: String(parsed.name || ""),
      subject: String(parsed.subject || ""),
      html_body: String(parsed.html_body || ""),
      editor_design:
        parsed.editor_design && typeof parsed.editor_design === "object"
          ? parsed.editor_design
          : null,
      goal_urls: Array.isArray(parsed.goal_urls)
        ? parsed.goal_urls.map(String)
        : [],
      use_brand_kit: Boolean(parsed.use_brand_kit),
    };
  } catch {
    return null;
  }
};

const writeDraft = (key: string, values: FormValues): void => {
  const hasContent =
    values.name.trim() || values.subject.trim() || values.html_body.trim();
  if (!hasContent) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify(values));
};

const clearDraft = (key: string): void => {
  localStorage.removeItem(key);
};

/**
 * Campanha — 3 modos: Editor (Easy Email) · Código · Importar URL (DOC 03 § 5.1).
 */
const CampaignForm = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [autoOpenTest, setAutoOpenTest] = useState(
    () => searchParams.get("openTest") === "1"
  );
  const isEdit = Boolean(id);

  useEffect(() => {
    if (searchParams.get("openTest") === "1") {
      setAutoOpenTest(true);
      const next = new URLSearchParams(searchParams);
      next.delete("openTest");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const draftKey = draftStorageKey(id);
  const htmlRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const brandKitDefault = Boolean(
    getLoggedinUser()?.tenant?.brand_kit?.apply_default
  );

  document.title = isEdit
    ? t("campaigns.form.documentTitleEdit")
    : t("campaigns.form.documentTitleNew");

  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [serverSaving, setServerSaving] = useState(false);
  const [serverSavedAt, setServerSavedAt] = useState<Date | null>(null);
  /** ISO do updated_at no servidor — detecta edição concorrente. */
  const [serverUpdatedAt, setServerUpdatedAt] = useState<string | null>(null);
  const httpAutosaveBusy = useRef(false);
  const lastHttpFingerprint = useRef("");
  /** Evita spam de toast a cada falha de rede no autosave silencioso. */
  const lastQuietFailToastAt = useRef(0);
  const [formError, setFormError] = useState<string | null>(null);
  /** 409 optimistic lock — alerta com botão Recarregar (sem F5). */
  const [conflictMode, setConflictMode] = useState(false);
  const [reloadingServer, setReloadingServer] = useState(false);
  const [activeTab, setActiveTab] = useState<ContentTab>("editor");
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const [draftPrompt, setDraftPrompt] = useState<DraftPayload | null>(null);
  const [serverBaseline, setServerBaseline] = useState<FormValues>({
    name: "",
    subject: "",
    html_body: "",
    editor_design: null,
    goal_urls: [],
    use_brand_kit: brandKitDefault,
  });
  const [initialValues, setInitialValues] = useState<FormValues>({
    name: "",
    subject: "",
    html_body: "",
    editor_design: null,
    goal_urls: [],
    use_brand_kit: brandKitDefault,
  });
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightResult, setPreflightResult] =
    useState<DeliverabilityCheckResult | null>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [preflightVisible, setPreflightVisible] = useState(false);
  const [editorRemountKey, setEditorRemountKey] = useState("boot");
  const [importUrl, setImportUrl] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string()
          .trim()
          .max(255, t("campaigns.form.validation.nameMax"))
          .required(t("campaigns.form.validation.nameRequired")),
        subject: Yup.string()
          .trim()
          .max(255, t("campaigns.form.validation.subjectMax"))
          .required(t("campaigns.form.validation.subjectRequired")),
        html_body: Yup.string().required(
          t("campaigns.form.validation.htmlRequired")
        ),
      }),
    [t, i18n.language]
  );

  const validation = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      await persistCampaign(values, { navigateAway: true });
    },
  });

  const buildPayload = (values: FormValues) => ({
    name: values.name.trim(),
    subject: values.subject.trim(),
    html_body: values.html_body,
    editor_design: values.editor_design,
    goal_urls: values.goal_urls,
    use_brand_kit: values.use_brand_kit,
  });

  const fingerprint = (values: FormValues): string =>
    JSON.stringify(buildPayload(values));

  /**
   * Salva no servidor. openTest → fica na edição e abre modal de teste (LAC-112).
   * navigateAway → volta à lista (Salvar clássico).
   */
  const persistCampaign = async (
    values: FormValues,
    opts: { openTest?: boolean; navigateAway?: boolean; quiet?: boolean } = {}
  ): Promise<number | null> => {
    const payload = buildPayload(values);
    if (!payload.name || !payload.subject || !payload.html_body.trim()) {
      if (!opts.quiet) {
        setFormError(t("campaigns.error.save"));
        validation.setTouched({
          name: true,
          subject: true,
          html_body: true,
        });
      }
      return null;
    }

    if (!opts.quiet) {
      setSaving(true);
    } else {
      setServerSaving(true);
    }
    setFormError(null);
    setConflictMode(false);
    try {
      if (isEdit && id) {
        // Optimistic lock no PATCH — quiet não precisa de GET a cada debounce.
        const res = await updateCampaign(Number(id), {
          ...payload,
          expected_updated_at: serverUpdatedAt || undefined,
        });
        if (!opts.quiet) {
          toast.success(res.message || t("campaigns.toast.updated"), {
            autoClose: 2500,
          });
        }
        setServerBaseline(values);
        setServerSavedAt(new Date());
        if (res.data?.updated_at) {
          setServerUpdatedAt(res.data.updated_at);
        }
        lastHttpFingerprint.current = fingerprint(values);
        clearDraft(draftKey);
        rememberLastCampaignEdit(Number(id));
        if (opts.openTest) {
          setAutoOpenTest(true);
        } else if (opts.navigateAway) {
          navigate("/campaigns");
        }
        return Number(id);
      }

      const res = await createCampaign(payload);
      const newId = res.data?.id;
      if (!opts.quiet) {
        toast.success(res.message || t("campaigns.toast.created"), {
          autoClose: 2500,
        });
      }
      clearDraft(draftKey);
      if (!newId) {
        navigate("/campaigns");
        return null;
      }
      rememberLastCampaignEdit(newId);
      lastHttpFingerprint.current = fingerprint(values);
      if (res.data?.updated_at) {
        setServerUpdatedAt(res.data.updated_at);
      }
      if (opts.openTest) {
        navigate(`/campaigns/${newId}/edit?openTest=1`, { replace: true });
      } else if (opts.navigateAway) {
        navigate("/campaigns");
      } else {
        navigate(`/campaigns/${newId}/edit`, { replace: true });
      }
      return newId;
    } catch (err: any) {
      if (err?.code === "CAMPAIGN_CONFLICT" || err?.status === 409) {
        const remoteAt =
          typeof err?.details?.updated_at === "string"
            ? err.details.updated_at
            : null;
        if (remoteAt) {
          setServerUpdatedAt(remoteAt);
        }
        setConflictMode(true);
        toast.warning(t("campaigns.form.draft.conflict"), {
          autoClose: 7000,
        });
        if (!opts.quiet) {
          setFormError(t("campaigns.form.draft.conflictReload"));
        }
        return null;
      }
      if (opts.quiet) {
        const now = Date.now();
        if (now - lastQuietFailToastAt.current > 30_000) {
          lastQuietFailToastAt.current = now;
          toast.warning(t("campaigns.form.draft.autosaveFailed"), {
            autoClose: 4000,
          });
        }
      } else {
        const details = err?.details;
        if (details && typeof details === "object") {
          const mapped: Record<string, string> = {};
          Object.keys(details).forEach((key) => {
            mapped[key] = Array.isArray(details[key])
              ? details[key][0]
              : String(details[key]);
          });
          validation.setErrors(mapped);
        }
        setFormError(err?.message || t("campaigns.error.save"));
      }
      return null;
    } finally {
      setSaving(false);
      setServerSaving(false);
    }
  };

  const reloadFromServer = async () => {
    if (!isEdit || !id) {
      return;
    }
    setReloadingServer(true);
    setFormError(null);
    try {
      const res = await getCampaign(Number(id));
      if (!res?.success || !res.data) {
        toast.error(t("campaigns.error.loadOne"), { autoClose: 4000 });
        return;
      }
      const fromServer: FormValues = {
        name: res.data.name || "",
        subject: res.data.subject || "",
        html_body: res.data.html_body || "",
        editor_design: res.data.editor_design || null,
        goal_urls: Array.isArray(res.data.goal_urls) ? res.data.goal_urls : [],
        use_brand_kit: Boolean(res.data.use_brand_kit),
      };
      setServerBaseline(fromServer);
      setInitialValues(fromServer);
      validation.setValues(fromServer);
      setServerUpdatedAt(res.data.updated_at || null);
      setEditorRemountKey(`reload-${res.data.id}-${Date.now()}`);
      lastHttpFingerprint.current = fingerprint(fromServer);
      clearDraft(draftKey);
      setDraftPrompt(null);
      setConflictMode(false);
      toast.success(t("campaigns.form.draft.reloaded"), { autoClose: 2500 });
    } catch (err: any) {
      toast.error(err?.message || t("campaigns.error.loadOne"), {
        autoClose: 4000,
      });
    } finally {
      setReloadingServer(false);
    }
  };

  useEffect(() => {
    if (!isEdit || !id) {
      const existing = readDraft(draftKey);
      if (
        existing &&
        (existing.name || existing.subject || existing.html_body)
      ) {
        setDraftPrompt(existing);
      }
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await getCampaign(Number(id));
        if (cancelled) {
          return;
        }
        if (!res?.success || !res.data) {
          setLoadError(t("campaigns.error.loadOne"));
          return;
        }
        const fromServer: FormValues = {
          name: res.data.name || "",
          subject: res.data.subject || "",
          html_body: res.data.html_body || "",
          editor_design: res.data.editor_design || null,
          goal_urls: Array.isArray(res.data.goal_urls)
            ? res.data.goal_urls
            : [],
          use_brand_kit: Boolean(res.data.use_brand_kit),
        };
        setServerBaseline(fromServer);
        setInitialValues(fromServer);
        setServerUpdatedAt(res.data.updated_at || null);
        setEditorRemountKey(`server-${res.data.id}-${Date.now()}`);
        rememberLastCampaignEdit(res.data.id);

        const existing = readDraft(draftKey);
        if (
          existing &&
          (existing.name !== fromServer.name ||
            existing.subject !== fromServer.subject ||
            existing.html_body !== fromServer.html_body)
        ) {
          setDraftPrompt(existing);
        }
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(err?.message || t("campaigns.error.loadOne"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, t, draftKey]);

  useEffect(() => {
    if (loading || loadError || draftPrompt) {
      return;
    }
    const timer = window.setTimeout(() => {
      writeDraft(draftKey, validation.values);
      const hasContent =
        validation.values.name.trim() ||
        validation.values.subject.trim() ||
        validation.values.html_body.trim();
      if (hasContent) {
        setDraftSavedAt(new Date());
      }
    }, AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [validation.values, draftKey, loading, loadError, draftPrompt]);

  // PATCH silencioso só com campanha já salva — evita criar lixo a cada tecla (LAC-112).
  useEffect(() => {
    if (!isEdit || !id || loading || loadError || draftPrompt || saving) {
      return;
    }
    const values = validation.values;
    if (
      !values.name.trim() ||
      !values.subject.trim() ||
      !values.html_body.trim()
    ) {
      return;
    }
    const fp = fingerprint(values);
    if (fp === lastHttpFingerprint.current || fp === fingerprint(serverBaseline)) {
      return;
    }
    const timer = window.setTimeout(() => {
      if (httpAutosaveBusy.current) {
        return;
      }
      httpAutosaveBusy.current = true;
      void (async () => {
        try {
          await persistCampaign(values, { quiet: true });
        } finally {
          httpAutosaveBusy.current = false;
        }
      })();
    }, HTTP_AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
    // persistCampaign fecha sobre state atual; debounce basta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    validation.values,
    isEdit,
    id,
    loading,
    loadError,
    draftPrompt,
    saving,
    serverBaseline,
  ]);

  useEffect(() => {
    if (isEdit && id && serverBaseline.name) {
      lastHttpFingerprint.current = fingerprint(serverBaseline);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, serverBaseline.name, serverBaseline.subject, serverBaseline.html_body]);

  const applyDraft = () => {
    if (!draftPrompt) {
      return;
    }
    setInitialValues(draftPrompt);
    setEditorRemountKey(`draft-${Date.now()}`);
    setDraftPrompt(null);
    toast.info(t("campaigns.form.draft.restored"), { autoClose: 2500 });
  };

  const discardDraft = () => {
    clearDraft(draftKey);
    setDraftPrompt(null);
    if (isEdit) {
      setInitialValues(serverBaseline);
      setEditorRemountKey(`discard-${Date.now()}`);
    }
  };

  const onEditorChange = useCallback(
    (payload: {
      editor_design: Record<string, unknown>;
      html_body: string;
    }) => {
      validation.setFieldValue("editor_design", payload.editor_design);
      validation.setFieldValue("html_body", payload.html_body);
    },
    // Formik setFieldValue é estável o suficiente para este uso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const insertPlaceholder = (token: string) => {
    const el = htmlRef.current;
    const current = validation.values.html_body || "";
    if (!el || typeof el.selectionStart !== "number") {
      validation.setFieldValue("html_body", current + token);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd ?? start;
    const next = current.slice(0, start) + token + current.slice(end);
    validation.setFieldValue("html_body", next);
    validation.setFieldValue("editor_design", null);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + token.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const submitImportUrl = async () => {
    setImportBusy(true);
    setImportError(null);
    try {
      const res = await fetchCampaignHtmlFromUrl(importUrl.trim());
      validation.setFieldValue("html_body", res.data.html_body);
      validation.setFieldValue("editor_design", null);
      toast.success(res.message || t("campaigns.form.importUrl.success"), {
        autoClose: 3000,
      });
      setImportUrl("");
      setActiveTab("source");
    } catch (err: any) {
      const details = err?.details?.url;
      setImportError(
        (Array.isArray(details) ? details[0] : null) ||
          err?.message ||
          t("campaigns.form.importUrl.error")
      );
    } finally {
      setImportBusy(false);
    }
  };

  const showFieldError = (field: keyof FormValues) =>
    Boolean(validation.touched[field] && validation.errors[field]);

  const draftLabel = useCallback(() => {
    if (serverSaving) {
      return t("campaigns.form.draft.savingServer");
    }
    if (serverSavedAt) {
      return t("campaigns.form.draft.savedServer", {
        time: serverSavedAt.toLocaleTimeString(),
      });
    }
    if (!draftSavedAt) {
      return null;
    }
    return t("campaigns.form.draft.saved", {
      time: draftSavedAt.toLocaleTimeString(),
    });
  }, [draftSavedAt, serverSavedAt, serverSaving, t]);

  const formReadyToSave = Boolean(
    validation.values.name.trim() &&
      validation.values.subject.trim() &&
      validation.values.html_body.trim()
  );

  const onSaveAndTest = () => {
    void persistCampaign(validation.values, { openTest: true });
  };

  const trackableUrls = useMemo(
    () => extractTrackableUrlsFromHtml(validation.values.html_body),
    [validation.values.html_body]
  );

  const toggleGoalUrl = (url: string) => {
    const current = validation.values.goal_urls;
    const next = current.includes(url)
      ? current.filter((u) => u !== url)
      : [...current, url];
    validation.setFieldValue("goal_urls", next);
  };

  const runPreflight = useCallback(async () => {
    if (!id) {
      return;
    }
    setPreflightVisible(true);
    setPreflightLoading(true);
    setPreflightError(null);
    try {
      const res = await checkCampaignDeliverability(Number(id), {
        subject: validation.values.subject,
        html_body: validation.values.html_body,
      });
      if (res?.success && res.data) {
        setPreflightResult(res.data);
      } else {
        setPreflightResult(null);
        setPreflightError(t("campaigns.error.loadOne"));
      }
    } catch (err: any) {
      setPreflightResult(null);
      setPreflightError(err?.message || t("preflight.retry"));
    } finally {
      setPreflightLoading(false);
    }
  }, [id, validation.values.subject, validation.values.html_body, t]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={
            isEdit ? t("campaigns.form.titleEdit") : t("campaigns.form.titleNew")
          }
          pageTitle={t("campaigns.breadcrumb")}
        />

        <ToastContainer closeButton={false} limit={1} />

        {loadError && (
          <Alert color="danger">
            {loadError}{" "}
            <Link to="/campaigns" className="alert-link">
              {t("campaigns.form.backToList")}
            </Link>
          </Alert>
        )}

        {draftPrompt && (
          <Alert
            color="info"
            className="d-flex flex-wrap gap-2 align-items-center justify-content-between"
          >
            <span>{t("campaigns.form.draft.found")}</span>
            <span className="d-flex gap-2">
              <Button color="primary" size="sm" onClick={applyDraft}>
                {t("campaigns.form.draft.restore")}
              </Button>
              <Button color="light" size="sm" onClick={discardDraft}>
                {t("campaigns.form.draft.discard")}
              </Button>
            </span>
          </Alert>
        )}

        {conflictMode && (
          <Alert
            color="warning"
            className="d-flex flex-wrap gap-2 align-items-center justify-content-between"
          >
            <span>{t("campaigns.form.draft.conflictReload")}</span>
            <Button
              color="primary"
              size="sm"
              type="button"
              disabled={reloadingServer}
              onClick={() => void reloadFromServer()}
            >
              {reloadingServer ? (
                <Spinner size="sm" className="me-1" />
              ) : null}
              {t("campaigns.form.draft.reloadFromServer")}
            </Button>
          </Alert>
        )}

        {formError && !conflictMode && (
          <Alert color="danger" toggle={() => setFormError(null)}>
            {formError}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          !loadError && (
            <Row>
              <Col lg={12}>
                <Card>
                  <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <h5 className="card-title mb-0">
                        {isEdit
                          ? t("campaigns.form.titleEdit")
                          : t("campaigns.form.titleNew")}
                      </h5>
                      {draftLabel() && (
                        <Badge color="light" className="text-muted">
                          {draftLabel()}
                        </Badge>
                      )}
                    </div>
                    <Nav tabs className="nav-tabs-custom mb-0 border-0">
                      {(
                        [
                          ["editor", "campaigns.form.tab.editor"],
                          ["source", "campaigns.form.tab.source"],
                          ["import", "campaigns.form.tab.import"],
                        ] as const
                      ).map(([tab, labelKey]) => (
                        <NavItem key={tab}>
                          <NavLink
                            href="#"
                            className={classnames({
                              active: activeTab === tab,
                            })}
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveTab(tab);
                            }}
                          >
                            {t(labelKey)}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>
                  </CardHeader>
                  <CardBody>
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                      }}
                    >
                      <Row className="g-3 mb-3">
                        <Col md={6}>
                          <Label htmlFor="campaign-name">
                            {t("campaigns.form.name")}
                          </Label>
                          <Input
                            id="campaign-name"
                            name="name"
                            value={validation.values.name}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={showFieldError("name")}
                            placeholder={t("campaigns.form.namePlaceholder")}
                          />
                          {showFieldError("name") && (
                            <FormFeedback type="invalid">
                              {validation.errors.name}
                            </FormFeedback>
                          )}
                          <p className="text-muted small mb-0 mt-1">
                            {t("campaigns.form.nameHelp")}
                          </p>
                        </Col>
                        <Col md={6}>
                          <Label htmlFor="campaign-subject">
                            {t("campaigns.form.subject")}
                          </Label>
                          <Input
                            id="campaign-subject"
                            name="subject"
                            value={validation.values.subject}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={showFieldError("subject")}
                            placeholder={t(
                              "campaigns.form.subjectPlaceholder"
                            )}
                          />
                          {showFieldError("subject") && (
                            <FormFeedback type="invalid">
                              {validation.errors.subject}
                            </FormFeedback>
                          )}
                          <p className="text-muted small mb-0 mt-1">
                            {t("campaigns.form.subjectHelp")}
                          </p>
                        </Col>
                      </Row>

                      <Row className="g-3 mb-3">
                        <Col md={12}>
                          <div className="form-check">
                            <Input
                              id="campaign-use-brand-kit"
                              type="checkbox"
                              className="form-check-input"
                              checked={validation.values.use_brand_kit}
                              onChange={(e) => {
                                validation.setFieldValue(
                                  "use_brand_kit",
                                  e.target.checked
                                );
                              }}
                            />
                            <Label
                              htmlFor="campaign-use-brand-kit"
                              className="form-check-label"
                            >
                              {t("campaigns.form.useBrandKit")}
                            </Label>
                          </div>
                          <p className="text-muted small mb-0 ms-4">
                            {t("campaigns.form.useBrandKitHint")}
                          </p>
                        </Col>
                      </Row>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId="editor">
                          {activeTab === "editor" && (
                            <CampaignEasyEmailEditor
                              design={validation.values.editor_design}
                              subject={validation.values.subject}
                              remountKey={editorRemountKey}
                              onChange={onEditorChange}
                              useBrandKit={validation.values.use_brand_kit}
                              showTestSend
                              autoOpenTest={autoOpenTest}
                              testCampaign={
                                isEdit && id
                                  ? {
                                      id: Number(id),
                                      name: validation.values.name,
                                      subject: validation.values.subject,
                                      html_body: validation.values.html_body,
                                      editor_design:
                                        validation.values.editor_design,
                                    }
                                  : null
                              }
                            />
                          )}
                        </TabPane>

                        <TabPane tabId="source">
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            <span className="text-muted small align-self-center">
                              {t("campaigns.form.placeholders.label")}
                            </span>
                            {PLACEHOLDERS.map((token) => (
                              <Button
                                key={token}
                                type="button"
                                color="light"
                                size="sm"
                                className="font-monospace"
                                onClick={() => insertPlaceholder(token)}
                              >
                                {token}
                              </Button>
                            ))}
                          </div>
                          <Label htmlFor="campaign-html">
                            {t("campaigns.form.htmlBody")}
                          </Label>
                          <Input
                            id="campaign-html"
                            name="html_body"
                            type="textarea"
                            rows={16}
                            className="font-monospace"
                            innerRef={htmlRef}
                            value={validation.values.html_body}
                            onChange={(e) => {
                              validation.handleChange(e);
                              validation.setFieldValue("editor_design", null);
                            }}
                            onBlur={validation.handleBlur}
                            invalid={showFieldError("html_body")}
                            placeholder={t("campaigns.form.htmlPlaceholder")}
                          />
                          {showFieldError("html_body") && (
                            <FormFeedback type="invalid">
                              {validation.errors.html_body}
                            </FormFeedback>
                          )}
                          <p className="text-muted small mb-0 mt-1">
                            {t("campaigns.form.htmlHelp")}
                          </p>
                          {validation.values.html_body.trim() && (
                            <div className="mt-3">
                              <p className="text-muted small mb-2">
                                {t("campaigns.form.preview.hint")}
                              </p>
                              <div
                                className="border rounded bg-white"
                                style={{ minHeight: 240 }}
                              >
                                <iframe
                                  title={t("campaigns.form.tab.source")}
                                  sandbox=""
                                  srcDoc={validation.values.html_body}
                                  style={{
                                    width: "100%",
                                    minHeight: 240,
                                    border: 0,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </TabPane>

                        <TabPane tabId="import">
                          <p className="text-muted">
                            {t("campaigns.form.importUrl.help")}
                          </p>
                          <Label htmlFor="campaign-import-url">
                            {t("campaigns.form.importUrl.label")}
                          </Label>
                          <Input
                            id="campaign-import-url"
                            type="url"
                            value={importUrl}
                            onChange={(e) => setImportUrl(e.target.value)}
                            placeholder="https://"
                            invalid={Boolean(importError)}
                            disabled={importBusy}
                          />
                          {importError && (
                            <FormFeedback className="d-block">
                              {importError}
                            </FormFeedback>
                          )}
                          <div className="mt-3">
                            <Button
                              color="primary"
                              type="button"
                              disabled={importBusy || !importUrl.trim()}
                              onClick={submitImportUrl}
                            >
                              {importBusy ? (
                                <Spinner size="sm" />
                              ) : (
                                t("campaigns.form.importUrl.submit")
                              )}
                            </Button>
                          </div>
                        </TabPane>
                      </TabContent>

                      {isEdit && id ? (
                        <Card className="mt-3 border">
                          <CardBody>
                            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                              <h6 className="mb-0">{t("preflight.title")}</h6>
                              <Button
                                color="soft-primary"
                                size="sm"
                                type="button"
                                disabled={preflightLoading}
                                onClick={() => void runPreflight()}
                              >
                                {preflightLoading ? (
                                  <Spinner size="sm" />
                                ) : (
                                  t("preflight.openButton")
                                )}
                              </Button>
                            </div>
                            {preflightVisible ? (
                              <PreflightPanel
                                result={preflightResult}
                                loading={preflightLoading}
                                error={preflightError}
                                onRecheck={() => void runPreflight()}
                              />
                            ) : null}
                          </CardBody>
                        </Card>
                      ) : null}

                      {trackableUrls.length > 0 ? (
                        <Card className="mt-3 border">
                          <CardBody>
                            <h6 className="mb-1">
                              {t("campaigns.form.goalUrls.title")}
                            </h6>
                            <p className="text-muted small mb-3">
                              {t("campaigns.form.goalUrls.hint")}
                            </p>
                            <div className="d-flex flex-column gap-2">
                              {trackableUrls.map((url) => (
                                <Label
                                  key={url}
                                  check
                                  className="d-flex align-items-start gap-2 mb-0"
                                >
                                  <Input
                                    type="checkbox"
                                    checked={validation.values.goal_urls.includes(
                                      url
                                    )}
                                    onChange={() => toggleGoalUrl(url)}
                                  />
                                  <span className="text-break small">{url}</span>
                                </Label>
                              ))}
                            </div>
                          </CardBody>
                        </Card>
                      ) : validation.values.html_body.trim() ? (
                        <Alert color="light" className="mt-3 mb-0 border">
                          <div className="fw-medium mb-1">
                            {t("campaigns.form.goalUrls.emptyTitle")}
                          </div>
                          <div className="text-muted small mb-0">
                            {t("campaigns.form.goalUrls.empty")}
                          </div>
                        </Alert>
                      ) : null}

                      <div className="d-flex flex-wrap gap-2 justify-content-end mt-4">
                        <Button
                          color="light"
                          tag={Link}
                          to="/campaigns"
                          disabled={saving || serverSaving}
                        >
                          {t("campaigns.form.cancel")}
                        </Button>
                        <Button
                          color="soft-success"
                          type="button"
                          disabled={saving || serverSaving || !formReadyToSave}
                          onClick={onSaveAndTest}
                          title={
                            formReadyToSave
                              ? undefined
                              : t("campaigns.form.saveAndTestDisabled")
                          }
                        >
                          {saving ? (
                            <Spinner size="sm" />
                          ) : (
                            t("campaigns.form.saveAndTest")
                          )}
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={saving || serverSaving || !formReadyToSave}
                        >
                          {saving ? (
                            <Spinner size="sm" />
                          ) : (
                            t("campaigns.form.save")
                          )}
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )
        )}
      </Container>
    </div>
  );
};

export default CampaignForm;
